// CleanBelt Console Dashboard
// Updated: 2026-04-12 - Refactored for maintainability
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';

// コンポーネント
import SensorManagement from './SensorManagement';
import Modal from './Modal';
import Login from './Login';
import KPICard from './components/KPICard';

// ページコンポーネント
import Overview from './pages/Dashboard/Overview';
import PendingEvents from './pages/Dashboard/PendingEvents';
import Companies from './pages/Management/Companies';
import Washers from './pages/Management/Washers';
import EventLogs from './pages/Management/EventLogs';
import Organizations from './pages/Management/Organizations';
import Locations from './pages/Management/Locations';

// 定数
import { API_BASE_URL } from './constants/config';

// API サービス
import {
  dashboardAPI,
  companiesAPI,
  pendingEventsAPI,
  organizationsAPI,
  locationsAPI,
  locationTypesAPI,
  washersAPI,
  eventLogsAPI
} from './services/api';

// スタイル

// ort './App.css';

function App() {
  // React Router フック
  const navigate = useNavigate();
  const location = useLocation();

  // 本番ドメインではHTTPアクセスをHTTPSへ強制する
  useEffect(() => {
    const { protocol, hostname, pathname, search, hash } = window.location;
    const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
    if (protocol === 'http:' && !isLocalDev) {
      window.location.replace(`https://${hostname}${pathname}${search}${hash}`);
    }
  }, []);
  
  // 認証状態
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [username, setUsername] = useState(() => {
    return localStorage.getItem('username') || '';
  });

  const handleLogin = () => {
    setIsAuthenticated(true);
    setUsername(localStorage.getItem('username') || 'admin');
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUsername('');
    navigate('/'); // ログアウト時はトップに戻る
  };

  // テーマ管理
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // ユーザーメニュードロップダウン
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = React.useRef(null);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  // テーマをdocumentに適用
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const [error, setError] = useState(null);
  
  // URLパスからアクティブメニューを決定
  const getActiveMenuFromPath = () => {
    const path = location.pathname;
    if (path.startsWith('/management')) return 'management';
    if (path.startsWith('/settings')) return 'settings';
    return 'dashboard';
  };
  
  const getDashboardSubTabFromPath = () => {
    const path = location.pathname;
    if (path === '/pending-events') return 'pending-events';
    return 'overview';
  };
  
  const getManagementSubTabFromPath = () => {
    const path = location.pathname;
    if (path === '/management/organizations') return 'organizations';
    if (path === '/management/locations') return 'locations';
    if (path === '/management/washers') return 'washers';
    if (path === '/management/sensors') return 'sensors';
    if (path === '/management/event-logs') return 'event-logs';
    return 'companies';
  };
  
  const activeGlobalMenu = getActiveMenuFromPath();
  const dashboardSubTab = getDashboardSubTabFromPath();
  const managementSubTab = getManagementSubTabFromPath();
  
  // 自動更新設定（LocalStorageに保存）
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('autoRefreshEnabled');
    return saved === null ? true : saved === 'true';
  });
  
  const toggleAutoRefresh = () => {
    const newValue = !autoRefreshEnabled;
    setAutoRefreshEnabled(newValue);
    localStorage.setItem('autoRefreshEnabled', newValue.toString());
  };
  
  // 後方互換性のためactiveTabも維持（既存コードで使用）
  const [activeTab] = useState('overview');
  
  // ダッシュボード用のstate
  const [dashboardData, setDashboardData] = useState({
    active_rfid_locations: 0,
    washing_count: 0,
    alert_count: 0,
    cleaning_histories: []
  });
  
  // 未紐付けイベント用のstate
  const [pendingEvents, setPendingEvents] = useState([]);
  const [selectedPendingEventIds, setSelectedPendingEventIds] = useState([]);
  const [pendingEventSort, setPendingEventSort] = useState({ column: 'event_timestamp', order: 'desc' });
  const [pendingEventPage, setPendingEventPage] = useState(() => {
    const saved = localStorage.getItem('pendingEventPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pendingEventPageSize, setPendingEventPageSize] = useState(() => {
    const saved = localStorage.getItem('pendingEventPageSize');
    return saved ? parseInt(saved, 10) : 50;
  });
  
  // 洗浄履歴ソート・フィルタリング用のstate
  const [historySort, setHistorySort] = useState({ column: 'started_at', order: 'desc' });
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historySearchText, setHistorySearchText] = useState('');
  const [historyPage, setHistoryPage] = useState(() => {
    const saved = localStorage.getItem('historyPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [historyPageSize, setHistoryPageSize] = useState(() => {
    const saved = localStorage.getItem('historyPageSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  
  // 会社管理用のstate
  const [companies, setCompanies] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    company_code: '',
    company_name: '',
    notes: ''
  });
  
  // 会社管理ページネーション・ソート用のstate
  const [companyPage, setCompanyPage] = useState(() => {
    const saved = localStorage.getItem('companyPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [companyPageSize, setCompanyPageSize] = useState(() => {
    const saved = localStorage.getItem('companyPageSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [companySort, setCompanySort] = useState(() => {
    const saved = localStorage.getItem('companySort');
    return saved ? JSON.parse(saved) : { column: 'company_id', order: 'asc' };
  });

  // 組織階層管理用のstate（localStorageから復元）
  const [selectedCompany, setSelectedCompany] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedCompany');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Failed to load selectedCompany from localStorage:', e);
      return null;
    }
  });
  const [organizationUnits, setOrganizationUnits] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [parentOrg, setParentOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({
    organization_unit_type: 'department',
    organization_unit_code: '',
    organization_unit_name: '',
    sort_order: 0,
    is_active: true
  });

  // 設置先階層管理用のstate
  const [locationNodes, setLocationNodes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showParentSelector, setShowParentSelector] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [parentLocation, setParentLocation] = useState(null);
  const [unassignedRfids, setUnassignedRfids] = useState([]); // 未割り当てRFID一覧
  const [locationForm, setLocationForm] = useState({
    location_node_type_id: '',
    location_code: '',
    location_name: '',
    rfid_tag_id: '',
    sort_order: 0,
    is_active: true
  });
  
  // 設置先種別マスタ管理用のstate
  const [showLocationTypeModal, setShowLocationTypeModal] = useState(false);

  // 洗浄機管理用のstate
  const [washers, setWashers] = useState([]);
  const [showWasherModal, setShowWasherModal] = useState(false);
  const [editingWasher, setEditingWasher] = useState(null);
  const [washerForm, setWasherForm] = useState({
    washer_code: '',
    washer_name: '',
    serial_number: '',
    model_name: '',
    status: 'active',
    notes: ''
  });
  const [showWasherHistory, setShowWasherHistory] = useState(false);
  const [washerAssignments, setWasherAssignments] = useState([]);
  const [selectedHistoryWasher, setSelectedHistoryWasher] = useState(null);
  
  // センサー管理用のstate
  const [showWasherAttachment, setShowWasherAttachment] = useState(false);
  const [attachingLocationNode, setAttachingLocationNode] = useState(null);
  const [isAttachingWasher, setIsAttachingWasher] = useState(false);

  // イベントログ用のstate
  const [eventLogs, setEventLogs] = useState([]);
  const [selectedEventLogIds, setSelectedEventLogIds] = useState([]);
  const [eventLogFilters, setEventLogFilters] = useState({
    error_only: false,
    event_type: [],
    mac_address: ''
  });
  const [eventLogSort, setEventLogSort] = useState({ column: 'received_at', order: 'desc' });
  const [eventLogPage, setEventLogPage] = useState(() => {
    const saved = localStorage.getItem('eventLogPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [eventLogPageSize, setEventLogPageSize] = useState(() => {
    const saved = localStorage.getItem('eventLogPageSize');
    return saved ? parseInt(saved, 10) : 50;
  });
  const [eventLogTotal, setEventLogTotal] = useState(0);
  const [eventLogSearchTrigger] = useState(0); // 後方互換のため残す（未使用）
  
  // センサー管理からイベントログ画面への遷移ハンドラ
  const handleNavigateToEventLogs = (macAddress) => {
    // フィルタを設定
    setEventLogFilters(prev => ({
      ...prev,
      mac_address: macAddress
    }));
    // 管理メニューのイベントログタブに遷移
    navigate('/management/event-logs');
  };
  
  // JSONモーダル用のstate
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonModalData, setJsonModalData] = useState(null);
  
  // イベントログ列設定（デフォルト定義）
  const defaultEventLogColumns = [
    { id: 'checkbox', label: '選択', visible: true, fixed: true, sortable: false, width: 60 },
    { id: 'event_log_id', label: 'ID', visible: true, fixed: false, sortable: true, width: 80 },
    { id: 'received_at', label: '受信日時', visible: true, fixed: false, sortable: true, width: 180 },
    { id: 'timestamp', label: '実行日時', visible: true, fixed: false, sortable: false, width: 180 },
    { id: 'mac_address', label: 'MACアドレス', visible: true, fixed: false, sortable: false, width: 180 },
    { id: 'event_type', label: 'イベント', visible: true, fixed: false, sortable: true, width: 120 },
    { id: 'rfid_tag_id', label: 'RFID', visible: true, fixed: false, sortable: false, width: 150 },
    { id: 'is_processed', label: '処理状態', visible: true, fixed: false, sortable: false, width: 100 },
    { id: 'result_type', label: '結果', visible: true, fixed: false, sortable: false, width: 100 },
    { id: 'result_detail', label: '詳細', visible: true, fixed: false, sortable: false, width: 250 },
    { id: 'json', label: 'JSON', visible: true, fixed: false, sortable: false, width: 200 },
    { id: 'cleaning_history_id', label: 'cleaning_history_id', visible: false, fixed: false, sortable: false, width: 150 },
    { id: 'pending_event_id', label: 'pending_event_id', visible: false, fixed: false, sortable: false, width: 150 },
    { id: 'actions', label: '操作', visible: true, fixed: true, sortable: false, width: 150 }
  ];

  const reorderEventLogColumns = (columns) => {
    const receivedIndex = columns.findIndex(col => col.id === 'received_at');
    const timestampIndex = columns.findIndex(col => col.id === 'timestamp');

    if (receivedIndex === -1 || timestampIndex === -1) {
      return columns;
    }

    const reordered = [...columns];
    const [timestampColumn] = reordered.splice(timestampIndex, 1);
    const targetIndex = reordered.findIndex(col => col.id === 'received_at');
    reordered.splice(targetIndex + 1, 0, {
      ...timestampColumn,
      visible: true
    });

    return reordered;
  };

  // localStorageから列設定を読み込む
  const loadEventLogColumns = () => {
    try {
      const saved = localStorage.getItem('eventLogColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultById = new Map(defaultEventLogColumns.map(col => [col.id, col]));
        const normalized = [];

        // 保存済み設定の表示・幅は引き継ぎ、ラベルは最新定義を優先
        parsed.forEach((savedCol) => {
          const base = defaultById.get(savedCol.id);
          if (!base) return;

          normalized.push({
            ...base,
            visible: savedCol.id === 'timestamp' ? true : (savedCol.visible ?? base.visible),
            sortable: savedCol.sortable ?? base.sortable,
            width: savedCol.width ?? base.width,
            fixed: savedCol.fixed ?? base.fixed
          });

          defaultById.delete(savedCol.id);
        });

        return reorderEventLogColumns([...normalized, ...Array.from(defaultById.values())]);
      }
    } catch (e) {
      console.error('Failed to load column settings:', e);
    }
    return reorderEventLogColumns(defaultEventLogColumns);
  };

  const [eventLogColumns, setEventLogColumns] = useState(loadEventLogColumns());

  // 列設定変更時にlocalStorageに保存（DataTableコンポーネント内で処理）

  // イベントログの列幅リサイズはDataTableコンポーネント内で処理

  // 会社管理列設定（デフォルト定義）
  const defaultCompanyColumns = [
    { id: 'company_id', label: '会社ID', visible: true, sortable: true, width: 100 },
    { id: 'company_code', label: '会社コード', visible: true, sortable: true, width: 150 },
    { id: 'company_name', label: '会社名', visible: true, sortable: true, width: 250 },
    { id: 'notes', label: '備考', visible: true, sortable: false, width: 250 },
    { id: 'created_at', label: '登録日時', visible: true, sortable: true, width: 180 },
    { id: 'actions', label: '操作', visible: true, sortable: false, width: 450, fixed: true }
  ];

  // localStorageから会社管理列設定を読み込む
  const loadCompanyColumns = () => {
    try {
      const saved = localStorage.getItem('companyColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultById = new Map(defaultCompanyColumns.map(col => [col.id, col]));
        const normalized = [];

        parsed.forEach((savedCol) => {
          const base = defaultById.get(savedCol.id);
          if (!base) return;

          normalized.push({
            ...base,
            visible: savedCol.visible ?? base.visible,
            sortable: savedCol.sortable ?? base.sortable,
            width: savedCol.width ?? base.width,
            fixed: savedCol.fixed ?? base.fixed
          });

          defaultById.delete(savedCol.id);
        });

        return [...normalized, ...Array.from(defaultById.values())];
      }
    } catch (e) {
      console.error('Failed to load company columns:', e);
    }
    return defaultCompanyColumns;
  };

  const [companyColumns, setCompanyColumns] = useState(loadCompanyColumns());
  
  // 会社管理ページング・ソート設定をLocalStorageに保存
  React.useEffect(() => {
    localStorage.setItem('companyPage', companyPage.toString());
  }, [companyPage]);
  
  React.useEffect(() => {
    localStorage.setItem('companyPageSize', companyPageSize.toString());
  }, [companyPageSize]);
  
  React.useEffect(() => {
    localStorage.setItem('companySort', JSON.stringify(companySort));
  }, [companySort]);

  // Overview の cleaning_histories 列設定（デフォルト定義）
  const defaultHistoryColumns = [
    { id: 'cleaning_history_id', label: 'ID', visible: true, sortable: true, width: 80 },
    { id: 'washer_name', label: '洗浄機', visible: true, sortable: true, width: 150 },
    { id: 'sensor_name', label: 'センサー', visible: true, sortable: true, width: 150 },
    { id: 'location_name', label: '設置先', visible: true, sortable: true, width: 200 },
    { id: 'rfid_tag_id', label: 'RFIDタグ', visible: true, sortable: true, width: 150 },
    { id: 'result_status', label: 'ステータス', visible: true, sortable: true, width: 120 },
    { id: 'started_at', label: '開始時刻', visible: true, sortable: true, width: 180 },
    { id: 'ended_at', label: '終了時刻', visible: true, sortable: true, width: 180 },
    { id: 'duration_seconds', label: '洗浄時間', visible: true, sortable: true, width: 120 }
  ];

  const loadHistoryColumns = () => {
    try {
      const saved = localStorage.getItem('historyColumns');
      if (saved) {
        const parsed = JSON.parse(saved);

        // 保存済み設定の表示状態や幅は引き継ぎつつ、ラベルは最新定義を優先する
        const defaultById = new Map(defaultHistoryColumns.map(col => [col.id, col]));
        const normalized = [];

        parsed.forEach((savedCol) => {
          const base = defaultById.get(savedCol.id);
          if (!base) return;

          normalized.push({
            ...base,
            visible: savedCol.visible ?? base.visible,
            sortable: savedCol.sortable ?? base.sortable,
            width: savedCol.width ?? base.width,
            fixed: savedCol.fixed ?? base.fixed
          });

          defaultById.delete(savedCol.id);
        });

        // 新規追加された列は末尾に補完
        return [...normalized, ...Array.from(defaultById.values())];
      }
    } catch (e) {
      console.error('Failed to load history columns:', e);
    }
    return defaultHistoryColumns;
  };

  const [historyColumns, setHistoryColumns] = useState(loadHistoryColumns());

  // 未紐付けイベント列設定（デフォルト定義）
  const defaultPendingEventColumns = [
    { id: 'checkbox', label: '', visible: true, sortable: false, width: 40, fixed: true },
    { id: 'pending_event_id', label: 'ID', visible: true, sortable: true, width: 80 },
    { id: 'event_timestamp', label: '発生時刻', visible: true, sortable: true, width: 180 },
    { id: 'sensor_name', label: 'センサー', visible: true, sortable: true, width: 150 },
    { id: 'rfid_tag_id', label: 'RFIDタグ', visible: true, sortable: true, width: 150 },
    { id: 'event_type', label: 'イベント', visible: true, sortable: true, width: 100 },
    { id: 'status', label: 'ステータス', visible: true, sortable: false, width: 150 },
    { id: 'pending_reason', label: '理由', visible: true, sortable: true, width: 150 },
    { id: 'company_name', label: '会社', visible: true, sortable: true, width: 150 },
    { id: 'washer_name', label: '洗浄機', visible: true, sortable: true, width: 150 },
    { id: 'location_name', label: '設置先', visible: true, sortable: true, width: 150 },
    { id: 'actions', label: '操作', visible: true, sortable: false, width: 180, fixed: true }
  ];

  const loadPendingEventColumns = () => {
    try {
      const saved = localStorage.getItem('pendingEventColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaultById = new Map(defaultPendingEventColumns.map(col => [col.id, col]));
        const normalized = [];

        parsed.forEach((savedCol) => {
          const base = defaultById.get(savedCol.id);
          if (!base) return;

          normalized.push({
            ...base,
            visible: savedCol.visible ?? base.visible,
            sortable: savedCol.sortable ?? base.sortable,
            width: savedCol.width ?? base.width,
            fixed: savedCol.fixed ?? base.fixed
          });

          defaultById.delete(savedCol.id);
        });

        return [...normalized, ...Array.from(defaultById.values())];
      }
    } catch (e) {
      console.error('Failed to load pending event columns:', e);
    }
    return defaultPendingEventColumns;
  };

  const [pendingEventColumns, setPendingEventColumns] = useState(loadPendingEventColumns());

  // 未紐付けイベント解決用のstate（既存設置先選択）
  const [showLocationSelectModal, setShowLocationSelectModal] = useState(false);
  const [pendingEventToResolve, setPendingEventToResolve] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocationForResolve, setSelectedLocationForResolve] = useState(null);

  // テーマの初期化（LocalStorageから読み込んで適用）
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // テーマが変更されたときにHTML要素に適用
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    fetchCompanies();
    
    if (activeGlobalMenu !== 'dashboard') {
      return;
    }
    
    fetchDashboardData();
    fetchPendingEvents();
    
    if (!autoRefreshEnabled) {
      return;
    }
    
    let interval = null;
    
    const startAutoRefresh = () => {
      if (interval) clearInterval(interval);
      
      if (document.visibilityState === 'visible') {
        interval = setInterval(() => {
          fetchDashboardData();
          fetchPendingEvents();
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
        fetchPendingEvents();
        startAutoRefresh();
      } else {
        if (interval) {
          clearInterval(interval);
          interval = null;
       }
      }
    };
    
    startAutoRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefreshEnabled, activeGlobalMenu, selectedCompany?.company_id]);

  // selectedCompanyをlocalStorageに保存
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
    }
  }, [selectedCompany]);

  // ページネーション設定をLocalStorageに保存
  useEffect(() => {
    localStorage.setItem('pendingEventPage', pendingEventPage.toString());
  }, [pendingEventPage]);

  useEffect(() => {
    localStorage.setItem('pendingEventPageSize', pendingEventPageSize.toString());
  }, [pendingEventPageSize]);

  useEffect(() => {
    localStorage.setItem('historyPage', historyPage.toString());
  }, [historyPage]);

  useEffect(() => {
    localStorage.setItem('historyPageSize', historyPageSize.toString());
  }, [historyPageSize]);

  useEffect(() => {
    localStorage.setItem('eventLogPage', eventLogPage.toString());
  }, [eventLogPage]);

  useEffect(() => {
    localStorage.setItem('eventLogPageSize', eventLogPageSize.toString());
  }, [eventLogPageSize]);

  // companiesが読み込まれた時、localStorageから会社を復元
  useEffect(() => {
    if (companies.length > 0 && selectedCompany) {
      // 保存されていた会社IDが現在の会社リストに存在するか確認
      const company = companies.find(c => c.company_id === selectedCompany.company_id);
      if (company) {
        // 最新の会社情報に更新
        setSelectedCompany(company);
      } else {
        // 存在しない場合は最初の会社を選択
        setSelectedCompany(companies[0]);
      }
    } else if (companies.length > 0 && !selectedCompany) {
      // 何も保存されていない場合は最初の会社を選択
      setSelectedCompany(companies[0]);
    }
  }, [companies]);

  // 管理タブで会社が未選択の場合、最初の会社を自動選択
  useEffect(() => {
    if ((activeTab === 'organizations' || activeTab === 'locations' || activeTab === 'washers') && 
        !selectedCompany && 
        companies.length > 0) {
      setSelectedCompany(companies[0]);
    }
  }, [activeTab, companies, selectedCompany]);

  // 選択された会社のデータを取得（全タブのデータを一度に取得）
  useEffect(() => {
    const loadAllData = async () => {
      // 会社が未選択の場合は何もしない
      if (!selectedCompany) {
        return;
      }
      
      try {
        // 組織階層データを取得
        const orgData = await organizationsAPI.getAll(selectedCompany.company_id);
        setOrganizationUnits(orgData);
        
        // 設置先種別マスタを取得
        const typesData = await locationTypesAPI.getAll(selectedCompany.company_id);
        setLocationTypes(typesData);
        
        // 未割り当てRFIDを取得
        try {
          const rfids = await locationsAPI.getUnassignedRfids(selectedCompany.company_id);
          setUnassignedRfids(rfids);
        } catch (err) {
          console.error('未割り当てRFIDの取得に失敗:', err);
          setUnassignedRfids([]);
        }
        
        // 設置先階層を取得
        const nodesData = await locationsAPI.getAll(selectedCompany.company_id);
        setLocationNodes(nodesData);
        
        // 洗浄機リストを取得
        const washersData = await washersAPI.getAll(selectedCompany.company_id);
        setWashers(washersData);
      } catch (err) {
        console.error(`データ取得に失敗:`, err);
      }
    };
    
    loadAllData();
  }, [selectedCompany]);
  
  // センサーイベントログの自動更新
  useEffect(() => {
    const isEventLogTabActive = activeGlobalMenu === 'management' && managementSubTab === 'event-logs';
    
    if (isEventLogTabActive) {
      fetchEventLogs();
      setSelectedEventLogIds([]);
    } else {
      return;
    }
    
    if (!autoRefreshEnabled) {
      return;
    }
    
    let interval = null;
    
    const startAutoRefresh = () => {
      if (interval) clearInterval(interval);
      
      if (document.visibilityState === 'visible') {
        interval = setInterval(() => {
          fetchEventLogs();
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchEventLogs();
        startAutoRefresh();
      } else {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };
    
    startAutoRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefreshEnabled, activeGlobalMenu, managementSubTab, eventLogPage, eventLogPageSize, eventLogSort, eventLogFilters]);
  
  const fetchDashboardData = async () => {
    try {
      const companyId = selectedCompany?.company_id;
      if (!companyId) {
        setDashboardData({
          active_rfid_locations: 0,
          washing_count: 0,
          alert_count: 0,
          cleaning_histories: []
        });
        return;
      }

      const data = await dashboardAPI.getOverview(companyId);
      setDashboardData(data);
    } catch (err) {
      console.error('ダッシュボードデータの取得に失敗:', err);
    }
  };
  
  const fetchPendingEvents = async () => {
    try {
      const companyId = selectedCompany?.company_id;
      if (!companyId) {
        setPendingEvents([]);
        return;
      }

      const data = await pendingEventsAPI.getAll(companyId);
      setPendingEvents(data);
    } catch (err) {
      console.error('未紐付けイベントの取得に失敗:', err);
    }
  };
  
  const handleDeletePendingEvent = async (pendingEventId) => {
    if (!window.confirm('この未紐付けイベントを削除してもよろしいですか？')) {
      return;
    }
    
    try {
      await pendingEventsAPI.delete(pendingEventId);
      // 再取得
      fetchPendingEvents();
      fetchDashboardData();
      setSelectedPendingEventIds([]);
      alert('未紐付けイベントを削除しました');
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };
  
  const handleDeleteSelectedPendingEvents = async () => {
    if (selectedPendingEventIds.length === 0) {
      alert('削除する項目を選択してください');
      return;
    }
    
    if (!window.confirm(`選択した${selectedPendingEventIds.length}件の未紐付けイベントを削除してもよろしいですか？`)) {
      return;
    }
    
    try {
      // 並列で削除リクエストを送信
      await Promise.all(
        selectedPendingEventIds.map(id => 
          pendingEventsAPI.delete(id)
        )
      );
      // 再取得
      fetchPendingEvents();
      fetchDashboardData();
      setSelectedPendingEventIds([]);
      alert('選択した未紐付けイベントを削除しました');
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };
  
  const handleResolvePendingEvent = async (pendingEventId) => {
    try {
      await pendingEventsAPI.resolve(pendingEventId);
      // 再取得
      fetchPendingEvents();
      fetchDashboardData();
      alert('紐付けを解決しました');
    } catch (err) {
      alert(`解決に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };
  
  const handleResolveWithLocation = async (pendingEventId, locationNodeId) => {
    try {
      await pendingEventsAPI.resolve(pendingEventId, locationNodeId);
      // 再取得
      fetchPendingEvents();
      fetchDashboardData();
      setShowLocationSelectModal(false);
      setPendingEventToResolve(null);
      setSelectedLocationForResolve(null);
      alert('紐付けを解決しました');
    } catch (err) {
      alert(`解決に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      setCompanies(data);
    } catch (err) {
      console.error('会社一覧の取得に失敗しました:', err);
      setError(`会社一覧の取得に失敗しました: ${err.message}`);
    }
  };

  const handleCreateCompany = () => {
    setEditingCompany(null);
    setCompanyForm({ company_code: '', company_name: '', notes: '' });
    setShowCompanyModal(true);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      company_code: company.company_code,
      company_name: company.company_name,
      notes: company.notes || ''
    });
    setShowCompanyModal(true);
  };

  const handleSaveCompany = async () => {
    try {
      if (editingCompany) {
        // 更新
        await companiesAPI.update(editingCompany.company_id, companyForm);
      } else {
        // 新規作成
        await companiesAPI.create(companyForm);
      }
      setShowCompanyModal(false);
      fetchCompanies();
    } catch (err) {
      alert(`保存に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('この会社を削除してもよろしいですか？')) return;
    
    try {
      await companiesAPI.delete(companyId);
      fetchCompanies();
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  // 組織階層管理のハンドラー
  // 階層構造の組織データをフラット化する補助関数
  const flattenOrganizationUnits = (units) => {
    const result = [];
    const traverse = (unitList) => {
      for (const unit of unitList) {
        result.push(unit);
        if (unit.children && unit.children.length > 0) {
          traverse(unit.children);
        }
      }
    };
    traverse(units);
    return result;
  };

  const handleManageOrganization = async (company, switchTab = true) => {
    setSelectedCompany(company);
    if (switchTab) {
      navigate('/management/organizations');
    }
    try {
      const data = await organizationsAPI.getAll(company.company_id);
      setOrganizationUnits(data);
    } catch (err) {
      console.error('組織一覧の取得に失敗しました:', err);
      setOrganizationUnits([]);
    }
  };

  const handleAddOrganization = (parentNode) => {
    setEditingOrg(null);
    setParentOrg(parentNode.organization_unit_id ? parentNode : null);
    
    // 兄弟ノードの数を確認して並び順を設定（1から始まる）
    const siblings = parentNode.children || [];
    const defaultSortOrder = siblings.length + 1;
    
    setOrgForm({
      organization_unit_type: 'department',
      organization_unit_code: '',
      organization_unit_name: '',
      sort_order: defaultSortOrder,
      is_active: true
    });
    setShowOrgModal(true);
  };

  const handleEditOrganization = (org) => {
    setEditingOrg(org);
    
    // 親組織の情報を取得してセット
    if (org.parent_organization_unit_id) {
      // 階層構造をフラット化して親組織を検索
      const flatUnits = flattenOrganizationUnits(organizationUnits);
      const parentUnit = flatUnits.find(unit => unit.organization_unit_id === org.parent_organization_unit_id);
      setParentOrg(parentUnit || null);
    } else {
      setParentOrg(null);
    }
    
    setOrgForm({
      organization_unit_type: org.organization_unit_type,
      organization_unit_code: org.organization_unit_code || '',
      organization_unit_name: org.organization_unit_name,
      sort_order: org.sort_order,
      is_active: org.is_active
    });
    setShowOrgModal(true);
  };

  const handleSaveOrganization = async () => {
    if (!orgForm.organization_unit_name.trim()) {
      alert('組織名を入力してください');
      return;
    }

    try {
      const data = {
        ...orgForm,
        parent_organization_unit_id: parentOrg?.organization_unit_id || null
      };

      if (editingOrg) {
        await organizationsAPI.update(editingOrg.organization_unit_id, data);
      } else {
        await organizationsAPI.create(selectedCompany.company_id, data);
      }
      
      setShowOrgModal(false);
      // 組織一覧を再取得
      const orgData = await organizationsAPI.getAll(selectedCompany.company_id);
      setOrganizationUnits(orgData);
    } catch (err) {
      alert(`保存に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteOrganization = async (org) => {
    if (!window.confirm(`「${org.organization_unit_name}」を削除してもよろしいですか？`)) return;
    
    try {
      await organizationsAPI.delete(org.organization_unit_id);
      // 組織一覧を再取得
      const data = await organizationsAPI.getAll(selectedCompany.company_id);
      setOrganizationUnits(data);
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ================================================================================
  // 設置先階層管理ハンドラー
  // ================================================================================

  // 階層構造のlocationNodesをフラット化する補助関数
  const flattenLocationNodes = (nodes) => {
    const result = [];
    const traverse = (nodeList) => {
      for (const node of nodeList) {
        result.push(node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    traverse(nodes);
    return result;
  };

  const handleManageLocations = async (company, switchTab = true) => {
    try {
      setSelectedCompany(company);
      
      // 設置先種別マスタを取得
      const typesData = await locationTypesAPI.getAll(company.company_id);
      setLocationTypes(typesData);
      
      // 未割り当てRFIDを取得
      try {
        const rfids = await locationsAPI.getUnassignedRfids(company.company_id);
        setUnassignedRfids(rfids);
      } catch (err) {
        console.error('未割り当てRFIDの取得に失敗:', err);
        setUnassignedRfids([]);
      }
      
      // 設置先階層を取得
      const nodesResponse = await locationsAPI.getAll(company.company_id);
      setLocationNodes(nodesResponse);
      
      // 洗浄機リストも取得（設置先階層画面での洗浄機表示用）
      const washersData = await washersAPI.getAll(company.company_id);
      setWashers(washersData);
      
      // 設置先階層タブに切り替え
      if (switchTab) {
        navigate('/management/locations');
      }
    } catch (err) {
      alert(`設置先情報の取得に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleAddLocation = async (parentNode) => {
    setEditingLocation(null);
    setParentLocation(parentNode);
    
    // 未割り当てRFIDを取得
    if (selectedCompany) {
      try {
      const rfids = await locationsAPI.getUnassignedRfids(selectedCompany.company_id);
      setUnassignedRfids(rfids);
      } catch (err) {
        console.error('未割り当てRFIDの取得に失敗:', err);
        setUnassignedRfids([]);
      }
    }
    
    // デフォルト値の設定
    let defaultTypeId = locationTypes.length > 0 ? locationTypes[0].location_node_type_id : '';
    let defaultSortOrder = 1;  // 並び順は1から始まる
    
    if (parentNode) {
      // 親ノードがある場合：親の次の階層種別を自動選択
      const parentType = locationTypes.find(t => t.location_node_type_id === parentNode.location_node_type_id);
      if (parentType) {
        // sort_orderが親の次の種別を探す
        const nextTypes = locationTypes.filter(t => t.sort_order > parentType.sort_order);
        if (nextTypes.length > 0) {
          // sort_orderが最小の次の階層を選択
          nextTypes.sort((a, b) => a.sort_order - b.sort_order);
          defaultTypeId = nextTypes[0].location_node_type_id;
        }
      }
      
      // 兄弟ノードの数を確認して並び順を設定（1から始まる）
      const siblings = parentNode.children || [];
      defaultSortOrder = siblings.length + 1;
    }
    
    setLocationForm({
      location_node_type_id: defaultTypeId,
      location_code: '',
      location_name: '',
      rfid_tag_id: '',
      sort_order: defaultSortOrder,
      is_active: true
    });
    setShowLocationModal(true);
  };

  const handleEditLocation = async (location) => {
    setEditingLocation(location);
    
    // 未割り当てRFIDを取得（編集時は現在のRFIDも含める）
    if (selectedCompany) {
      try {
        let rfids = await locationsAPI.getUnassignedRfids(selectedCompany.company_id);
        
        // 編集時：現在のRFIDが未割り当てリストにない場合は追加
        if (location.rfid_tag_id && !rfids.find(r => r.rfid_tag_id === location.rfid_tag_id)) {
          rfids.unshift({
            rfid_tag_id: location.rfid_tag_id,
            last_seen: location.updated_at || location.created_at
          });
        }
        
        setUnassignedRfids(rfids);
      } catch (err) {
        console.error('未割り当てRFIDの取得に失敗:', err);
        // 編集時は少なくとも現在のRFIDを表示
        if (location.rfid_tag_id) {
          setUnassignedRfids([{
            rfid_tag_id: location.rfid_tag_id,
            last_seen: location.updated_at || location.created_at
          }]);
        } else {
          setUnassignedRfids([]);
        }
      }
    }
    
    // 親設置先の情報を取得してセット
    if (location.parent_location_node_id) {
      // 階層構造をフラット化して親ノードを検索
      const flatNodes = flattenLocationNodes(locationNodes);
      const parentNode = flatNodes.find(node => node.location_node_id === location.parent_location_node_id);
      setParentLocation(parentNode || null);
    } else {
      setParentLocation(null);
    }
    
    setLocationForm({
      location_node_type_id: location.location_node_type_id,
      location_code: location.location_code || '',
      location_name: location.location_name,
      rfid_tag_id: location.rfid_tag_id || '',
      sort_order: location.sort_order,
      is_active: location.is_active
    });
    setShowLocationModal(true);
  };

  const handleSaveLocation = async () => {
    try {
      if (editingLocation) {
        // 更新
        await locationsAPI.update(
          editingLocation.location_node_id,
          {
            ...locationForm,
            parent_location_node_id: parentLocation?.location_node_id || null
          }
        );
      } else {
        // 新規登録
        await locationsAPI.create(
          selectedCompany.company_id,
          {
            ...locationForm,
            parent_location_node_id: parentLocation?.location_node_id || null
          }
        );
      }
      
      // 設置先階層を再取得
      const data = await locationsAPI.getAll(selectedCompany.company_id);
      setLocationNodes(data);
      setShowLocationModal(false);
      
      // ダッシュボードデータも再取得（洗浄履歴に反映）
      fetchDashboardData();
      // 未紐付けイベントも再取得（RFIDが紐付けられた場合、自動解決される）
      fetchPendingEvents();
    } catch (err) {
      alert(`保存に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteLocation = async (location) => {
    if (!window.confirm(`「${location.location_name}」を削除してもよろしいですか？`)) return;
    
    try {
      await locationsAPI.delete(location.location_node_id);
      // 設置先階層を再取得
      const data = await locationsAPI.getAll(selectedCompany.company_id);
      setLocationNodes(data);
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ================================================================================
  // 設置先種別マスタ管理ハンドラー
  // ================================================================================

  const handleManageLocationTypes = () => {
    setShowLocationTypeModal(true);
  };

  const handleAddLocationType = async (typeData) => {
    try {
      await locationTypesAPI.create(
        selectedCompany.company_id,
        typeData
      );
      
      // 設置先種別マスタを再取得
      const data = await locationTypesAPI.getAll(selectedCompany.company_id);
      setLocationTypes(data);
    } catch (err) {
      alert(`登録に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleEditLocationType = async (typeId, typeData) => {
    try {
      await locationTypesAPI.update(
        typeId,
        typeData
      );
      
      // 設置先種別マスタを再取得
      const data = await locationTypesAPI.getAll(selectedCompany.company_id);
      setLocationTypes(data);
    } catch (err) {
      alert(`更新に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteLocationType = async (typeId) => {
    try {
      await locationTypesAPI.delete(typeId);
      
      // 設置先種別マスタを再取得
      const data = await locationTypesAPI.getAll(selectedCompany.company_id);
      setLocationTypes(data);
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  // ==================== 洗浄機管理ハンドラー ====================

  const handleManageWashers = async (company, switchTab = true) => {
    if (switchTab) {
      navigate('/management/washers');
    }
    setSelectedCompany(company);
    try {
      // 洗浄機一覧を取得
      const washersData = await washersAPI.getAll(company.company_id);
      setWashers(washersData);
      
      // 設置先階層も取得（洗浄機設置で使用）
      const locationsData = await locationsAPI.getAll(company.company_id);
      setLocationNodes(locationsData);
      
      // 設置先種別も取得
      const typesData = await locationTypesAPI.getAll(company.company_id);
      setLocationTypes(typesData);
    } catch (err) {
      alert(`洗浄機一覧の取得に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleAddWasher = () => {
    setEditingWasher(null);
    setWasherForm({
      washer_code: '',
      washer_name: '',
      serial_number: '',
      model_name: '',
      status: 'active',
      notes: ''
    });
    setShowWasherModal(true);
  };

  const handleEditWasher = (washer) => {
    setEditingWasher(washer);
    setWasherForm({
      washer_code: washer.washer_code,
      washer_name: washer.washer_name,
      serial_number: washer.serial_number || '',
      model_name: washer.model_name || '',
      status: washer.status,
      notes: washer.notes || ''
    });
    setShowWasherModal(true);
  };

  const handleSaveWasher = async () => {
    try {
      if (editingWasher) {
        // 更新
        await washersAPI.update(editingWasher.washer_id, washerForm);
      } else {
        // 新規作成
        await washersAPI.create(selectedCompany.company_id, washerForm);
      }
      
      // 洗浄機一覧を再取得
      const data = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(data);
      
      setShowWasherModal(false);
    } catch (err) {
      alert(`保存に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteWasher = async (washer) => {
    if (!window.confirm(`洗浄機「${washer.washer_name}」を削除しますか？`)) {
      return;
    }
    
    try {
      await washersAPI.delete(washer.washer_id);
      
      // 洗浄機一覧を再取得
      const data = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(data);
    } catch (err) {
      alert(`削除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleAssignWasher = async (washer, locationNode) => {
    try {
      // 洗浄機を設置（組織なしで設置可能）
      await washersAPI.assign(washer.washer_id, {
        location_node_id: locationNode.location_node_id,
        organization_unit_id: null,  // 組織は任意
        assignment_type: 'permanent'
      });
      
      // 洗浄機一覧を再取得
      const data = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(data);
      
      alert('洗浄機を設置しました');
    } catch (err) {
      alert(`設置に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleUnassignWasher = async (washer) => {
    if (!washer.current_assignment_id) {
      alert('この洗浄機は設置されていません');
      return;
    }
    
    if (!window.confirm(`洗浄機「${washer.washer_name}」の設置を解除しますか？`)) {
      return;
    }
    
    try {
      await washersAPI.unassign(washer.current_assignment_id);
      
      // 洗浄機一覧を再取得
      const data = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(data);
      
      alert('設置を解除しました');
    } catch (err) {
      alert(`設置解除に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleViewWasherHistory = async (washer) => {
    try {
      const data = await washersAPI.getHistory(washer.washer_id);
      setWasherAssignments(data);
      setSelectedHistoryWasher(washer);
      setShowWasherHistory(true);
    } catch (err) {
      alert(`設置履歴の取得に失敗しました: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleAttachWasherToLocation = (locationNode) => {
    setAttachingLocationNode(locationNode);
    setShowWasherAttachment(true);
  };

  const handleSelectWasherForAttachment = async (washer) => {
    // 処理中の場合は何もしない（二重送信防止）
    if (isAttachingWasher) {
      return;
    }
    
    try {
      setIsAttachingWasher(true);
      
      // 洗浄機を設置（組織なしで設置可能）
      await washersAPI.assign(washer.washer_id, {
        location_node_id: attachingLocationNode.location_node_id,
        organization_unit_id: null,  // 組織は任意
        assignment_type: 'permanent'
      });
      
      // 洗浄機リストを再取得（washersMapの更新用）
      const washersData = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(washersData);
      
      setShowWasherAttachment(false);
      setAttachingLocationNode(null);
      alert('洗浄機を設置しました');
    } catch (err) {
      alert(`設置に失敗しました: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsAttachingWasher(false);
    }
  };

  const handleDetachWasherFromLocation = async (washer) => {
    if (!washer.current_assignment_id) {
      alert('この洗浄機は設置されていません');
      return;
    }
    
    if (!window.confirm(`洗浄機「${washer.washer_name}」の設置を解除しますか？`)) {
      return;
    }
    
    // 処理中の場合は何もしない（二重送信防止）
    if (isAttachingWasher) {
      return;
    }
    
    try {
      setIsAttachingWasher(true);
      
      await washersAPI.unassign(washer.current_assignment_id);
      
      // 洗浄機リストを再取得
      const washersData = await washersAPI.getAll(selectedCompany.company_id);
      setWashers(washersData);
      
      setShowWasherAttachment(false);
      setAttachingLocationNode(null);
      alert('設置を解除しました');
    } catch (err) {
      alert(`設置解除に失敗しました: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsAttachingWasher(false);
    }
  };

  // ログインしていない場合はログイン画面を表示
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h3>エラー</h3>
          <p>{error}</p>
          <pre>API Base URL: {API_BASE_URL}</pre>
        </div>
      </div>
    );
  }

  const kpis = {
    total: 0, // ライン数は0のまま（決まってOK）
    running: dashboardData.active_rfid_locations,
    cleaning: dashboardData.washing_count,
    alerts: dashboardData.alert_count
  };

  // イベントログ取得
  const fetchEventLogs = async () => {
    try {
      const filters = {
        error_only: eventLogFilters.error_only,
        event_type: eventLogFilters.event_type,
        mac_address: eventLogFilters.mac_address,
        limit: eventLogPageSize,
        offset: (eventLogPage - 1) * eventLogPageSize,
        sort_by: eventLogSort.column,
        sort_order: eventLogSort.order
      };
      
      const response = await eventLogsAPI.getAll(filters);
      
      // APIは常に {logs: [...], total: number} 形式で返す
      setEventLogs(response.logs || []);
      setEventLogTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch event logs:', error);
      alert('イベントログの取得に失敗しました');
    }
  };

  // イベントログ検索（後方互換のため残す）
  const handleEventLogSearch = () => {
    setEventLogPage(1);
  };

  // イベントログ再処理
  const handleReprocessEventLog = async (eventLogId) => {
    if (!window.confirm('このイベントを再処理しますか？')) return;
    
    try {
      await eventLogsAPI.reprocess(eventLogId);
      alert('再処理が完了しました');
      fetchEventLogs();
    } catch (error) {
      console.error('Failed to reprocess event log:', error);
      alert('再処理に失敗しました: ' + (error.response?.data?.detail || error.message));
    }
  };

  // イベントログ削除
  const handleDeleteEventLog = async (eventLogId) => {
    if (!window.confirm('このイベントログを削除しますか？')) return;
    
    try {
      await eventLogsAPI.delete(eventLogId);
      alert('イベントログを削除しました');
      fetchEventLogs();
    } catch (error) {
      console.error('Failed to delete event log:', error);
      alert('削除に失敗しました');
    }
  };

  // イベントログの選択はDataTableコンポーネント内で処理

  // 選択したイベントログを一括削除
  const handleDeleteSelectedEventLogs = async () => {
    if (selectedEventLogIds.length === 0) {
      alert('削除する項目を選択してください');
      return;
    }

    if (!window.confirm(`選択した${selectedEventLogIds.length}件のイベントログを削除してもよろしいですか？`)) {
      return;
    }

    try {
      // 並列削除
      await Promise.all(
        selectedEventLogIds.map(id => 
          eventLogsAPI.delete(id)
        )
      );
      
      alert(`${selectedEventLogIds.length}件のイベントログを削除しました`);
      setSelectedEventLogIds([]);
      fetchEventLogs();
    } catch (error) {
      console.error('Failed to delete event logs:', error);
      alert('一部のイベントログの削除に失敗しました');
      fetchEventLogs();
    }
  };

  return (
    <div className="App" data-theme={theme}>
      {/* グローバルナビゲーションバー */}
      <nav>
        <a className="nb" href="/">
          <img 
            src="/iwot.png" 
            alt="IwoT Console" 
            width="28" 
            height="28" 
            style={{verticalAlign: 'middle', marginRight: '5px', borderRadius: '6px'}} 
          />
          IwoT Console
        </a>
        <a 
          className={activeGlobalMenu === 'dashboard' ? 'on' : ''}
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          href="/"
        >
          📊 ダッシュボード
        </a>
        <a 
          className={activeGlobalMenu === 'management' ? 'on' : ''}
          onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
          href="/management/companies"
        >
          🏢 管理
        </a>
        
        {/* 右側のツールバー */}
        <div className="nav-toolbar">
          {/* 自動更新トグル */}
          <div className="auto-refresh-toggle">
            <span className="refresh-status">
              {autoRefreshEnabled ? '🔄 自動更新' : '⏸️ 停止中'}
            </span>
            <button 
              className={`refresh-button ${autoRefreshEnabled ? 'active' : ''}`}
              onClick={toggleAutoRefresh}
            >
              {autoRefreshEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
          
          {/* 対象会社選択 */}
          <div className="company-selector">
            <label className="company-label">対象会社:</label>
            <select
              className="company-select"
              value={selectedCompany?.company_id || ''}
              onChange={(e) => {
                const companyId = parseInt(e.target.value);
                const company = companies.find(c => c.company_id === companyId);
                if (company) {
                  setSelectedCompany(company);
                }
              }}
            >
              <option value="">会社を選択してください</option>
              {companies.map(company => (
                <option key={company.company_id} value={company.company_id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* ユーザーメニュー */}
          <div className="user-menu-wrapper" ref={userMenuRef}>
            <button 
              className="user-menu-trigger"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="ユーザーメニュー"
            >
              <div className="user-avatar">
                {username.charAt(0).toUpperCase()}
              </div>
              <span className="user-name">{username}</span>
              <svg 
                className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`}
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="currentColor"
              >
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {showUserMenu && (
              <div className="user-dropdown-menu">
                <div className="dropdown-user-header">
                  <div className="dropdown-user-avatar">
                    {username.charAt(0).toUpperCase()}
                  </div>
                  <div className="dropdown-user-info">
                    <div className="dropdown-user-name">{username}</div>
                    <div className="dropdown-user-role">管理者</div>
                  </div>
                </div>
                
                <div className="dropdown-separator"></div>
                
                <button 
                  className="dropdown-menu-item theme-toggle-item"
                  onClick={() => {
                    toggleTheme();
                  }}
                >
                  <span className="menu-item-icon">
                    {theme === 'light' ? '🌙' : '☀️'}
                  </span>
                  <span className="menu-item-text">
                    {theme === 'light' ? 'ダークモード' : 'ライトモード'}
                  </span>
                  <span className="menu-item-badge">
                    {theme === 'light' ? 'Light' : 'Dark'}
                  </span>
                </button>
                
                <div className="dropdown-separator"></div>
                
                <button 
                  className="dropdown-menu-item logout-menu-item"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                >
                  <span className="menu-item-icon">🚪</span>
                  <span className="menu-item-text">ログアウト</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ルーティング */}
      <Routes>
        {/* ダッシュボード: Overview */}
        <Route path="/" element={
          <>
            {/* KPIカード */}
            <div className="w">
              <div className="kpi-container">
                <KPICard title="稼働中" value={kpis.running} kind="good" />
                <KPICard title="洗浄中" value={kpis.cleaning} kind="info" />
                <KPICard title="アラート" value={kpis.alerts} kind="bad" />
              </div>
            </div>

            {/* ダッシュボードサブメニュー */}
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={dashboardSubTab === 'overview' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  href="/"
                >
                  Overview
                </a>
                <a 
                  className={dashboardSubTab === 'pending-events' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/pending-events'); }}
                  href="/pending-events"
                >
                  未紐付けイベント
                  {dashboardData.alert_count > 0 && (
                    <span className="badge badge-error">
                      {dashboardData.alert_count}
                    </span>
                  )}
                </a>
              </div>
            </div>

            {/* ダッシュボードコンテンツ */}
            <div className="w">
              <Overview
                theme={theme}
                dashboardData={dashboardData}
                selectedCompany={selectedCompany}
                historyDateFrom={historyDateFrom}
                setHistoryDateFrom={setHistoryDateFrom}
                historyDateTo={historyDateTo}
                setHistoryDateTo={setHistoryDateTo}
                historyStatusFilter={historyStatusFilter}
                setHistoryStatusFilter={setHistoryStatusFilter}
                historySearchText={historySearchText}
                setHistorySearchText={setHistorySearchText}
                historyColumns={historyColumns}
                defaultHistoryColumns={defaultHistoryColumns}
                setHistoryColumns={setHistoryColumns}
                historySort={historySort}
                setHistorySort={setHistorySort}
                historyPage={historyPage}
                historyPageSize={historyPageSize}
                setHistoryPage={setHistoryPage}
                setHistoryPageSize={setHistoryPageSize}
              />
            </div>
          </>
        } />

        {/* ダッシュボード: 未紐付けイベント */}
        <Route path="/pending-events" element={
          <>
            {/* KPIカード */}
            <div className="w">
              <div className="kpi-container">
                <KPICard title="稼働中" value={kpis.running} kind="good" />
                <KPICard title="洗浄中" value={kpis.cleaning} kind="info" />
                <KPICard title="アラート" value={kpis.alerts} kind="bad" />
              </div>
            </div>

            {/* ダッシュボードサブメニュー */}
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={dashboardSubTab === 'overview' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  href="/"
                >
                  Overview
                </a>
                <a 
                  className={dashboardSubTab === 'pending-events' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/pending-events'); }}
                  href="/pending-events"
                >
                  未紐付けイベント
                  {dashboardData.alert_count > 0 && (
                    <span className="badge badge-error">
                      {dashboardData.alert_count}
                    </span>
                  )}
                </a>
              </div>
            </div>

            {/* コンテンツ */}
            <div className="w">
              <PendingEvents
                pendingEvents={pendingEvents}
                setPendingEvents={setPendingEvents}
                pendingEventSort={pendingEventSort}
                setPendingEventSort={setPendingEventSort}
                pendingEventPage={pendingEventPage}
                setPendingEventPage={setPendingEventPage}
                pendingEventPageSize={pendingEventPageSize}
                setPendingEventPageSize={setPendingEventPageSize}
                pendingEventColumns={pendingEventColumns}
                setPendingEventColumns={setPendingEventColumns}
                defaultPendingEventColumns={defaultPendingEventColumns}
                selectedPendingEventIds={selectedPendingEventIds}
                setSelectedPendingEventIds={setSelectedPendingEventIds}
                companies={companies}
                setPendingEventToResolve={setPendingEventToResolve}
                setAvailableLocations={setAvailableLocations}
                setSelectedLocationForResolve={setSelectedLocationForResolve}
                setShowLocationSelectModal={setShowLocationSelectModal}
                setSelectedCompany={setSelectedCompany}
                setLocationTypes={setLocationTypes}
                setUnassignedRfids={setUnassignedRfids}
                setLocationNodes={setLocationNodes}
                setWashers={setWashers}
                setEditingLocation={setEditingLocation}
                setParentLocation={setParentLocation}
                setLocationForm={setLocationForm}
                setShowLocationModal={setShowLocationModal}
                handleResolvePendingEvent={handleResolvePendingEvent}
                handleDeletePendingEvent={handleDeletePendingEvent}
                handleDeleteSelectedPendingEvents={handleDeleteSelectedPendingEvents}
                fetchDashboardData={fetchDashboardData}
                fetchPendingEvents={fetchPendingEvents}
              />
            </div>
          </>
        } />

        {/* 管理: 会社管理 */}
        <Route path="/management/companies" element={
          <>
            {/* 管理サブメニュー */}
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>

            {/* コンテンツ */}
            <div className="w">
              <Companies
                companies={companies}
                companyColumns={companyColumns}
                setCompanyColumns={setCompanyColumns}
                defaultCompanyColumns={defaultCompanyColumns}
                companySort={companySort}
                setCompanySort={setCompanySort}
                companyPage={companyPage}
                setCompanyPage={setCompanyPage}
                companyPageSize={companyPageSize}
                setCompanyPageSize={setCompanyPageSize}
                showCompanyModal={showCompanyModal}
                setShowCompanyModal={setShowCompanyModal}
                editingCompany={editingCompany}
                companyForm={companyForm}
                setCompanyForm={setCompanyForm}
                handleCreateCompany={handleCreateCompany}
                handleSaveCompany={handleSaveCompany}
                handleEditCompany={handleEditCompany}
                handleDeleteCompany={handleDeleteCompany}
                handleManageOrganization={handleManageOrganization}
                handleManageLocations={handleManageLocations}
                handleManageWashers={handleManageWashers}
                setSelectedCompany={setSelectedCompany}
                navigate={navigate}
              />
            </div>
          </>
        } />

        {/* 管理: 組織階層 */}
        <Route path="/management/organizations" element={
          <>
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>
            <div className="w">
              <Organizations
                selectedCompany={selectedCompany}
                organizationUnits={organizationUnits}
                handleAddOrganization={handleAddOrganization}
                handleEditOrganization={handleEditOrganization}
                handleDeleteOrganization={handleDeleteOrganization}
                showOrgModal={showOrgModal}
                setShowOrgModal={setShowOrgModal}
                editingOrg={editingOrg}
                parentOrg={parentOrg}
                orgForm={orgForm}
                setOrgForm={setOrgForm}
                handleSaveOrganization={handleSaveOrganization}
                theme={theme}
              />
            </div>
          </>
        } />

        {/* 管理: 設置先管理 */}
        <Route path="/management/locations" element={
          <>
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>
            <div className="w">
              <Locations
                selectedCompany={selectedCompany}
                locationNodes={locationNodes}
                locationTypes={locationTypes}
                handleAddLocation={handleAddLocation}
                handleEditLocation={handleEditLocation}
                handleDeleteLocation={handleDeleteLocation}
                handleManageLocationTypes={handleManageLocationTypes}
                handleAttachWasherToLocation={handleAttachWasherToLocation}
                washers={washers}
                showLocationModal={showLocationModal}
                setShowLocationModal={setShowLocationModal}
                editingLocation={editingLocation}
                parentLocation={parentLocation}
                setParentLocation={setParentLocation}
                locationForm={locationForm}
                setLocationForm={setLocationForm}
                handleSaveLocation={handleSaveLocation}
                showParentSelector={showParentSelector}
                setShowParentSelector={setShowParentSelector}
                unassignedRfids={unassignedRfids}
                theme={theme}
                showLocationTypeModal={showLocationTypeModal}
                setShowLocationTypeModal={setShowLocationTypeModal}
                handleAddLocationType={handleAddLocationType}
                handleEditLocationType={handleEditLocationType}
                handleDeleteLocationType={handleDeleteLocationType}
                showWasherAttachment={showWasherAttachment}
                setShowWasherAttachment={setShowWasherAttachment}
                attachingLocationNode={attachingLocationNode}
                isAttachingWasher={isAttachingWasher}
                handleDetachWasherFromLocation={handleDetachWasherFromLocation}
                handleSelectWasherForAttachment={handleSelectWasherForAttachment}
              />
            </div>
          </>
        } />

        {/* 管理: 洗浄機 */}
        <Route path="/management/washers" element={
          <>
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>
            <div className="w">
              <Washers
                selectedCompany={selectedCompany}
                washers={washers}
                locationNodes={locationNodes}
                locationTypes={locationTypes}
                handleAddWasher={handleAddWasher}
                handleEditWasher={handleEditWasher}
                handleDeleteWasher={handleDeleteWasher}
                handleAssignWasher={handleAssignWasher}
                handleUnassignWasher={handleUnassignWasher}
                handleViewWasherHistory={handleViewWasherHistory}
                theme={theme}
                showWasherModal={showWasherModal}
                setShowWasherModal={setShowWasherModal}
                editingWasher={editingWasher}
                washerForm={washerForm}
                setWasherForm={setWasherForm}
                handleSaveWasher={handleSaveWasher}
              />
            </div>
          </>
        } />

        {/* 管理: センサー */}
        <Route path="/management/sensors" element={
          <>
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>
            <div className="w">
              <SensorManagement 
                selectedCompany={selectedCompany}
                onClose={null}
                onNavigateToLogs={handleNavigateToEventLogs}
                theme={theme}
                autoRefreshEnabled={autoRefreshEnabled}
              />
            </div>
          </>
        } />

        {/* 管理: イベントログ */}
        <Route path="/management/event-logs" element={
          <>
            <div className="w">
              <div className="sub-nav">
                <a 
                  className={managementSubTab === 'companies' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/companies'); }}
                  href="/management/companies"
                >
                  会社管理
                </a>
                <a 
                  className={managementSubTab === 'organizations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/organizations'); }}
                  href="/management/organizations"
                >
                  組織階層
                </a>
                <a 
                  className={managementSubTab === 'locations' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/locations'); }}
                  href="/management/locations"
                >
                  設置先管理
                </a>
                <a 
                  className={managementSubTab === 'washers' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/washers'); }}
                  href="/management/washers"
                >
                  洗浄機
                </a>
                <a 
                  className={managementSubTab === 'sensors' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/sensors'); }}
                  href="/management/sensors"
                >
                  センサー
                </a>
                <a 
                  className={managementSubTab === 'event-logs' ? 'on' : ''}
                  onClick={(e) => { e.preventDefault(); navigate('/management/event-logs'); }}
                  href="/management/event-logs"
                >
                  イベントログ
                </a>
              </div>
            </div>
            <div className="w">
              <EventLogs
                theme={theme}
                eventLogFilters={eventLogFilters}
                setEventLogFilters={setEventLogFilters}
                eventLogColumns={eventLogColumns}
                eventLogs={eventLogs}
                defaultEventLogColumns={defaultEventLogColumns}
                setEventLogColumns={setEventLogColumns}
                eventLogSort={eventLogSort}
                setEventLogSort={setEventLogSort}
                selectedEventLogIds={selectedEventLogIds}
                setSelectedEventLogIds={setSelectedEventLogIds}
                eventLogPage={eventLogPage}
                eventLogPageSize={eventLogPageSize}
                eventLogTotal={eventLogTotal}
                setEventLogPage={setEventLogPage}
                setEventLogPageSize={setEventLogPageSize}
                handleDeleteSelectedEventLogs={handleDeleteSelectedEventLogs}
                setJsonModalData={setJsonModalData}
                setShowJsonModal={setShowJsonModal}
                handleReprocessEventLog={handleReprocessEventLog}
                handleDeleteEventLog={handleDeleteEventLog}
              />
            </div>
          </>
        } />

        {/* デフォルトルート（404など） */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* 設置先選択モーダル（RFIDタグ未検出イベントの解決用） */}
      {showLocationSelectModal && pendingEventToResolve && (
        <Modal
          title="設置先を選択"
          onClose={() => setShowLocationSelectModal(false)}
          width="600px"
        >
            <p style={{ marginBottom: '20px', color: '#718096', fontSize: '14px' }}>
              RFIDタグが読み取れなかったため、既存の設置先から選択してください。
            </p>
            
            <div className="form-group">
              <label>RFID設置先一覧</label>
              <div style={{ 
                maxHeight: '500px', 
                overflowY: 'auto', 
                border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #e2e8f0', 
                borderRadius: '6px',
                background: theme === 'dark' ? '#252d3a' : '#fff'
              }}>
                {availableLocations.map((location) => (
                  <div
                    key={location.location_node_id}
                    onClick={() => setSelectedLocationForResolve(location)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      backgroundColor: selectedLocationForResolve?.location_node_id === location.location_node_id ? (theme === 'dark' ? '#1a3a52' : '#e6f7ff') : (theme === 'dark' ? '#252d3a' : 'white'),
                      borderBottom: theme === 'dark' ? '1px solid #3d4858' : '1px solid #e2e8f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedLocationForResolve?.location_node_id !== location.location_node_id) {
                        e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2f3844' : '#f7fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedLocationForResolve?.location_node_id !== location.location_node_id) {
                        e.currentTarget.style.backgroundColor = theme === 'dark' ? '#252d3a' : 'white';
                      }
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '4px' }}>
                      {location.locationPath}
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{location.location_name}</div>
                    <div style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
                      RFID: {location.rfid_tag_id} | コード: {location.location_code}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowLocationSelectModal(false)}
                style={{ flex: 1 }}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedLocationForResolve}
                onClick={() => {
                  if (selectedLocationForResolve) {
                    handleResolveWithLocation(
                      pendingEventToResolve.pending_event_id,
                      selectedLocationForResolve.location_node_id
                    );
                  }
                }}
                style={{ flex: 1 }}
              >
                この設置先で解決
              </button>
            </div>
        </Modal>
      )}

      {/* JSONモーダル */}
      {showJsonModal && (
        <Modal
          title="センサーイベントJSON"
          onClose={() => setShowJsonModal(false)}
          width="800px"
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(jsonModalData, null, 2));
                  alert('JSONをクリップボードにコピーしました');
                }}
                className="btn btn-sm btn-secondary"
                style={{ fontSize: '12px' }}
              >
                📋 コピー
              </button>
            </div>
            
            <pre style={{
              backgroundColor: theme === 'dark' ? '#1e293b' : '#f7fafc',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '13px',
              lineHeight: '1.6',
              overflow: 'auto',
              maxHeight: '500px'
            }}>
              {JSON.stringify(
                typeof jsonModalData === 'string' ? JSON.parse(jsonModalData) : jsonModalData,
                null,
                2
              )}
            </pre>
            
            <button
              className="btn btn-secondary"
              onClick={() => setShowJsonModal(false)}
              style={{ marginTop: '16px', width: '100%' }}
            >
              閉じる
            </button>
        </Modal>
      )}
    </div>
  );
}

export default App;
